
import React, { useState, useEffect, useRef, useMemo } from "react";
import httpClient from "../../../../api/httpClient";
import { useDispatch, useSelector } from "react-redux";
import { CreditCard, Wallet, Truck, DollarSign, MapPin,Trash2, CheckCircle, XCircle, Calendar } from "lucide-react";import StickyHeader from "../../../../components/ModuleCommon/StickyHeader/StickyHeader";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { invoiceApi } from "../InvoiceApi";
import notify from "../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import Cookies from "js-cookie";
import Button from "../../../../components/ACS-Dashboard/ACS-Blocks/Button";
import { setLocalChargeStatus } from "../../../../redux/slices/invoiceSlice";
import { usePrivilege } from "../../../../guard/usePrivilege";
import { isCarrierOrAgentOrg } from "../../../../guard/privilegeUtils";
import { DisplayDetail } from "../../../../components/ACS-Dashboard/ACS-Blocks/DisplayDetail";
import { ExpandableHeader } from "../../../../components/ACS-Dashboard/ACS-Blocks/ExpandableHeader";
import { fallbackName } from "../Utils/util";



const GreyField = ({ label, value }) => (
  <div>
    <p className="text-xs text-gray-500">{label}</p>
    <input
      readOnly
      value={value ?? ""}
      className="w-full border border-gray-300 rounded px-2 py-1 min-h-[2.5rem] bg-[#e6e6f9] text-gray-900"
    />
  </div>
);

const ViewInvoiceTab = ({ invoice, onClose, onSave }) => {
  const dispatch = useDispatch();
  const overrides = useSelector((s) => s.invoices.chargeAuditOverrides);
  const listViewState = useSelector((s) => s.invoices.listViewState || {});
  const chargePageSize =
    listViewState["my-invoices"]?.itemsPerPage ||
    listViewState["charges-audit"]?.itemsPerPage ||
    50;
  const officeCode = Cookies.get("officeCode") || "DIAMOND-META";
  const API = process.env.REACT_APP_API_BASE_URL + "/api";

  const [fullInvoice, setFullInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({}); // which shipment indexes are open
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(64);
  const [editingChargesMode, setEditingChargesMode] = useState(false);
  const [editedShipments, setEditedShipments] = useState([]);
  const [editedValues, setEditedValues] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [toleranceMap, setToleranceMap] = useState({});
  const [origChargeMap, setOrigChargeMap] = useState({});


  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentData, setPaymentData] = useState([{
    checkNumber: "",
    payDate: "",
    paymentAmount: "",
    currency: "USD", 
  }]);
  const paymentDatePickersRef = useRef({}); 

const [showReasonModal, setShowReasonModal] = useState(false);
const [reasonAction, setReasonAction] = useState(null); // "reject" or "recall"
const [reasonNote, setReasonNote] = useState("");
  


  const { hasPrivilege } = usePrivilege();

  function getCurrentUser() {
    try {
      const token = Cookies.get("authToken");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.username || payload.email || payload.sub;
    } catch {
      return null;
    }
  }

  const isCarrierOrAgent = isCarrierOrAgentOrg();
  const canApproveInvoice = hasPrivilege("invoices:APPROVE") && !isCarrierOrAgent;
  const canRejectInvoice = hasPrivilege("invoices:REJECT") && !isCarrierOrAgent;

  const canApproveCharge = hasPrivilege("invoices:APPROVE") && !isCarrierOrAgent;
  const canRejectCharge = hasPrivilege("invoices:REJECT") && !isCarrierOrAgent;
  const canEditInvoice = hasPrivilege("invoices:EDIT");
  const canRecallInvoice = hasPrivilege("invoices:RECALL");

  


    
  const isShipper = canApproveInvoice || canRejectInvoice;

  const formatMoney = (amount, currency) => {
    if (amount == null || amount === "") return null;
    const n = Number(amount);
    if (Number.isNaN(n)) return null;
  
    const safe =
      typeof currency === "string" ? currency.trim().toUpperCase() : "";
    const code = /^[A-Z]{3}$/.test(safe) ? safe : "USD";
  
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${n.toFixed(2)} ${code}`;
    }
  };

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL + "/api";

  const TYPE_MAP = {
    "Base Charge":    "COST",
    "Buy Rate":       "BUY_RATE",
    "Sell Rate":      "SELL_RATE",
    "Revenue Charge": "REVENUE",
  };

  const formatTolerance = (chargeCode) => {
    const t = toleranceMap[chargeCode];
    if (!t) return "—";
    if (t.toleranceBasis === "FLAT")
      return formatMoney(t.toleranceValue, fullInvoice?.currency) ?? "—";
    if (t.toleranceBasis === "PERCENT") return `${t.toleranceValue}%`;
    return "—";
  };
  function normalizeType(raw) {
    return TYPE_MAP[raw] || "COST";
  }
  useEffect(() => {
    if (fullInvoice && !fullInvoice.payments) {
      setFullInvoice(prev => ({ ...prev, payments: [] }));
    }
  }, [fullInvoice]);

  useEffect(() => {
    const billToCode = fullInvoice?.billToOfficeCode;
    if (!billToCode) return;
    httpClient
      .get(`${API_BASE_URL}/invoice-charges/tolerance`, {
        headers: {
          Authorization: `Bearer ${Cookies.get("authToken")}`,
          "Office-Code": billToCode,
        },
      })
      .then(({ data }) => {
        if (!Array.isArray(data)) return;
        const map = {};
        data
          .filter(t => t.shipperCode === billToCode)
          .forEach(t => {
            if (t.chargeCode) map[t.chargeCode] = t;
          });
        setToleranceMap(map);
      })
      .catch(err => console.error("Failed to load tolerances:", err));
  }, [fullInvoice?.billToOfficeCode]);
  useEffect(() => {
    (async () => {
      const id = invoice.uniqueId || invoice.id;
            // if (!id) {
      //   console.error("No valid invoice ID");
      //   setLoading(false);
      //   return;
      // }
      try {
              
      const endpoint = invoice.uniqueId || invoice.id
        ? `${API_BASE_URL}/invoices/${encodeURIComponent(id)}`
        : `${API_BASE_URL}/invoices/number/${encodeURIComponent(invoice.invoiceNumber)}`;
      
      const resp = await httpClient.get(
        endpoint,
        {
          headers: {
            Authorization: `Bearer ${Cookies.get("authToken")}`,
            "Office-Code": invoice?.payToOfficeCode
          },
        }
      );

      setFullInvoice(resp.data ?? resp);
      } catch {
        setFullInvoice(invoice);
      } finally {
        setLoading(false);
      }
    })();
  }, [invoice]);

  useEffect(() => {
    const measure = () => {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.offsetHeight);
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);



  const shipments = useMemo(() => {
    if (!fullInvoice) return [];

        if (Array.isArray(fullInvoice.shipments) && fullInvoice.shipments.length) {
      return fullInvoice.shipments.map(shipment => {
        // Extract shipToInfo from shipTos array
        let shipToInfo = [];
        
        if (Array.isArray(shipment.shipTos) && shipment.shipTos.length) {
          // Map shipTos to shipToInfo format
          shipToInfo = shipment.shipTos.map(st => ({
            shipToCompanyName: st.companyName,
            shipToAddress: st.address,
            shipToCity: st.city,
            shipToState: st.state,
            shipToZip: st.zip,
            shipToCountry: st.country,
            shipToPhone: st.phone,
            shipToEmail: st.email,
            shipToFax: st.fax,
          }));
        } else if (Array.isArray(shipment.shipToInfo)) {
          shipToInfo = shipment.shipToInfo;
        } else if (shipment.shipToInfo) {
          shipToInfo = [shipment.shipToInfo];
        } else {
          // Fallback to legacy fields
          shipToInfo = [{
            shipToCompanyName: shipment.shipToCompanyName,
            shipToAddress: shipment.shipToAddress,
            shipToCity: shipment.shipToCity,
            shipToState: shipment.shipToState,
            shipToZip: shipment.shipToZip,
            shipToCountry: shipment.shipToCountry,
            shipToPhone: shipment.shipToPhone,
            shipToEmail: shipment.shipToEmail,
            shipToFax: shipment.shipToFax,
          }];
        }

        return {
          ...shipment,
          shipToInfo
        };
      });
    }

    return [
      {
        shipmentNumber: fullInvoice.shipmentNumber,
        shipmentOrigin: fullInvoice.shipmentOrigin,
        shipmentDestination: fullInvoice.shipmentDestination,
        equipmentNumber: fullInvoice.equipmentNumber,
        shipmentCharge: fullInvoice.shipmentCharge,
        charges: Array.isArray(fullInvoice.charges) && fullInvoice.charges.length
          ? fullInvoice.charges
          : [{
              chargeId: "fallback",
              chargeAmount: fullInvoice.chargeAmount,
              chargeCurrency: fullInvoice.chargeCurrency,
              chargeType: fullInvoice.chargeType,
              chargeCode: fullInvoice.chargeCode,
              chargeAuditStatus: fullInvoice.chargeAuditStatus,
              discrepancyStatus: fullInvoice.discrepancyStatus,
            }],
        shipToInfo: [{
          shipToCompanyName: fullInvoice.shipToCompanyName,
          shipToAddress: fullInvoice.shipToAddress,
          shipToCity: fullInvoice.shipToCity,
          shipToState: fullInvoice.shipToState,
          shipToZip: fullInvoice.shipToZip,
          shipToCountry: fullInvoice.shipToCountry,
          shipToPhone: fullInvoice.shipToPhone,
          shipToEmail: fullInvoice.shipToEmail,
          shipToFax: fullInvoice.shipToFax,
        }]
      },
    ];
  }, [fullInvoice]);

  useEffect(() => {
    if (!fullInvoice || !shipments.length) return;
    const token = Cookies.get("authToken");
    const billToCode = fullInvoice.billToOfficeCode;
    if (!billToCode) return;
  
    const shipmentNumbers = shipments.map(s => s.shipmentNumber).filter(Boolean);
  
    Promise.all(
      shipmentNumbers.map(sn =>
        httpClient.get(`${API}/shipments/detailed-shipment/${sn}`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": billToCode,
          },
        }).then(r => {
          const baseline = {};
          [...(r.data.charges || []), ...(r.data.estimatedCharges || [])].forEach(c => {
            const code = c.code;
            const amount = parseFloat(c.amount) || 0;
            if (!baseline[code]) baseline[code] = [];
            baseline[code].push(amount);
          });
          return [sn, baseline];
        }).catch(() => [sn, {}])
      )
    ).then(entries => {
      setOrigChargeMap(Object.fromEntries(entries));
    });
  }, [fullInvoice, shipments]);


  const getEstimatedAmount = (shipmentNumber, chargeCode) => {
    const amounts = origChargeMap?.[shipmentNumber]?.[chargeCode];
    if (!Array.isArray(amounts) || amounts.length === 0) return null;
    // PARENT invoices compare against the total across all charge rows for that code.
    if ((fullInvoice?.invoiceType || "").toUpperCase() === "PARENT") {
      return amounts.reduce((s, a) => s + a, 0);
    }
    return amounts[0];
  };

  const handleDeleteDraftInvoice = async () => {
    try {
      await httpClient.delete(
        `${API}/invoices/delete-invoice/${fullInvoice.uniqueId}`,
        {
          headers: {
            "Office-Code": fullInvoice.billToOfficeCode,
            Authorization: `Bearer ${Cookies.get("authToken")}`,
          },
        }
      );
  
      notify.success({
        title: "Invoice deleted",
        message: "Draft invoice has been permanently deleted.",
      });
      
      setShowDeleteConfirm(false);
      onSave?.();
      onClose?.();
    } catch (err) {
      console.error("Delete failed:", err);
      notify.error({
        title: "Delete failed",
        message: err?.response?.data?.message || "Could not delete invoice.",
      });
      setShowDeleteConfirm(false);
    }
  };
  
  
  const allChargesResolved = useMemo(
    () =>
      shipments.every((sh) =>
        (sh.charges || []).every(
          (c) =>
            c.discrepancyStatus === "Resolved" ||
            c.discrepancyStatus === "No Discrepancy"
        )
      ),
    [shipments]
  );

  const handleStartEditCharges = () => {
    setEditedShipments(JSON.parse(JSON.stringify(shipments)));
    setEditingChargesMode(true);
  };
  
  const handleCancelEditCharges = () => {
    setEditedShipments([]);
    setEditingChargesMode(false);
  };
  
  const handleEditedChargeChange = (i, j, field, value) => {
    setEditedShipments(prev =>
      prev.map((s, si) => {
        if (si !== i) return s;
        return {
          ...s,
          charges: s.charges.map((c, ci) => (ci === j ? { ...c, [field]: value } : c)),
        };
      })
    );
  };
  
  const handleSaveEditedCharges = async () => {
    try {
      const editedShipmentNumbers = Array.from(
        new Set((editedShipments || []).map(s => s.shipmentNumber).filter(Boolean))
      );

      let shipmentData = [];
      if (editedShipmentNumbers.length) {
        try {
          shipmentData = await Promise.all(
            editedShipmentNumbers.map(sn =>
              httpClient
                .get(`${API}/shipments/${sn}`, {
                  headers: {
                    Authorization: `Bearer ${Cookies.get("authToken")}`,
                    "Office-Code": officeCode
                  }
                })
                .then(r => r.data)
            )
          );
        } catch (fetchErr) {
          // fallback to invoice's shipments if external fetch fails
          console.warn("Failed to fetch shipments; falling back to invoice.shipments", fetchErr);
          shipmentData = fullInvoice.shipments || [];
        }
      } else {
        shipmentData = fullInvoice.shipments || [];
      }

      const shipmentMap = new Map();
      shipmentData.forEach(sh => {
        const map = {};
        (sh.charges || []).forEach(sc => {
          const code = sc.code ?? sc.chargeCode ?? sc.chargeId ?? "_unknown_";
          map[code] = Number(sc.amount ?? sc.chargeAmount ?? 0);
        });
        shipmentMap.set(sh.shipmentNumber, map);
      });

      const payloadShipments = (editedShipments || []).map(s => {
        const origMap = shipmentMap.get(s.shipmentNumber) || {};
        const charges = (s.charges || []).map(c => {
          const entered = Number(c.chargeAmount || 0);
          const orig = typeof origMap[c.chargeCode] !== "undefined" ? origMap[c.chargeCode] : undefined;

          const hasDisc =
            orig === undefined ? entered > 0 : Math.abs(entered - Number(orig || 0)) > 0.0001;

          const diff = orig === undefined ? (hasDisc ? entered : 0) : Math.abs((Number(orig) || 0) - entered);
          

          return {
            ...c,
            chargeAmount: entered,
            chargeCurrency: c.chargeCurrency || "USD",
            chargeAuditStatus: hasDisc ? "Failed" : "Approved",
            discrepancyStatus: hasDisc ? "Discrepancy" : "No Discrepancy",
            discrepancyAmount: diff.toFixed(2),
          };
        });

        return {
          ...s,
          shipmentCharge: parseFloat(s.shipmentCharge) || 0,
          charges,
        };
      });

      const currentStatus = (fullInvoice.invoiceStatus || "").toLowerCase();
      let newStatus = fullInvoice.invoiceStatus;
      if (currentStatus === "draft" || currentStatus === "rejected") {
        newStatus = "Pending";
      }

      const today = new Date().toISOString().split("T")[0];

      const payload = {
        ...fullInvoice,
        invoiceStatus: newStatus,
        shipments: payloadShipments,
        invoiceDate: today , // attach today's date on resubmit
      };

      const updated = await httpClient.put(
        `${API}/invoices/update-invoice/${fullInvoice.uniqueId}`,
        payload,
        {
          headers: {
            "Office-Code": fullInvoice.payToOfficeCode,
            Authorization: `Bearer ${Cookies.get("authToken")}`,
          },
        }
      ).then(res => res.data);

      // 7) UI updates
      setFullInvoice(updated);
      setEditingChargesMode(false);
      setEditedShipments([]);
      notify.success({
        title: "Invoice updated",
        message:
          newStatus === "Pending"
            ? "Charges saved and invoice resubmitted. Shipments marked invoiced."
            : "Charges saved.",
      });
      onSave?.();
    } catch (err) {
      console.error("Save edited charges failed:", err);
      notify.error({
        title: "Save failed",
        message: err?.response?.data?.message || "Could not save edited charges.",
      });
    }
  };

  const handleApproveInvoice = async (uniqueId, passToParent = false) => {
    try {
      const inv = fullInvoice;

      const invoiceNumber = inv.invoiceNumber;



        const getAuthToken = () =>
            localStorage.getItem("authToken") || Cookies.get("authToken");


      const updatedInv = await httpClient.put(
        `${API}/invoices/update-invoice/${uniqueId}`,
        { ...inv, invoiceStatus: "Approved" ,
        approvalDate: new Date().toISOString(),
        approvedBy: getCurrentUser(),
        passToParent,
          },
        {
          headers: {
            "Office-Code": inv.billToOfficeCode,
            Authorization: `Bearer ${getAuthToken()}`,
          },
        }
      ).then(res => res.data);
      
      setFullInvoice(updatedInv);

      if ((updatedInv.invoiceStatus || "").toLowerCase() === "approved") {
        notify.success({ title: "Invoice approved", message: "Shipment charges synced successfully." });
      } else {
        notify.warning({
          title: "Approval blocked",
          message:
            "Invoice could not be approved — one or more charges still have discrepancies. " +
            "Resolve all charge discrepancies and try again.",
        });
      }
    } catch (err) {
      console.error("Approve failed:", err);
      notify.error({
        title: "Approval failed",
        message: err?.response?.data?.message || "See console for details or try again."
      });
    }
  };
  
  // const handleRejectInvoice = async () => {
  //   try {
  //     const updated = await axios.put(
  //       `${API}/invoices/update-invoice/${fullInvoice.uniqueId}`,
  //       { ...fullInvoice, invoiceStatus: "Rejected",
  //       rejectedDate: new Date().toISOString(),    
  //       rejectedBy: getCurrentUser(),              
  //           },
  //       {
  //         headers: {
  //           "Office-Code": fullInvoice.billToOfficeCode,
  //           Authorization: `Bearer ${Cookies.get("authToken")}`,
  //         },
  //       }
  //     ).then(res => res.data);
      
  //     setFullInvoice(updated);
  
  //       
  //     notify.warning({  title: "Invoice rejected", message: "Linked shipments marked as not invoiced."    });
  //     onSave?.();
  //   } catch (e) {
  //     console.error("Reject failed:", e);
  //     notify.error({
  //          title: "Reject failed",
  //         message: e?.response?.data?.message || "Could not reject invoice."
  //        });
  //   }
  // };
  

  const handleRejectInvoice = async () => {
    setReasonAction("reject");
    setReasonNote("");
    setShowReasonModal(true);
  };
  
  const performRejectInvoice = async () => {
    try {
      const updated = await httpClient.put(
        `${API}/invoices/update-invoice/${fullInvoice.uniqueId}`,
        { ...fullInvoice, invoiceStatus: "Rejected",
        rejectedDate: new Date().toISOString(),
        rejectedBy: getCurrentUser(),
        rejectReason: reasonNote
            },
        {
          headers: {
            "Office-Code": fullInvoice.billToOfficeCode,
            Authorization: `Bearer ${Cookies.get("authToken")}`,
          },
        }
      ).then(res => res.data);
      
      setFullInvoice(updated);
  
        
      notify.warning({  title: "Invoice rejected", message: "Linked shipments marked as not invoiced."    });
      onSave?.();
    } catch (e) {
      console.error("Reject failed:", e);
      notify.error({
           title: "Reject failed",
          message: e?.response?.data?.message || "Could not reject invoice."
         });
    }
  };

  // const handleRecallInvoice = async () => {
    
  //   try {
  //     const invNo = fullInvoice.invoiceNumber;
  //              
  //     await axios.put(
  //       `${API}/invoices/${invNo}/recall`,
  //       {},
  //       { headers: { Authorization: `Bearer ${Cookies.get("authToken")}`, "Office-Code": fullInvoice.billToOfficeCode } }
  //     );
      
  //     notify.success("✅ Invoice moved back to Drafts");
  //     onSave?.();
  //     onClose?.();
  //   } catch (err) {
  //     console.error("Recall failed:", err);
  //     notify.error("❌ Could not recall invoice. Please try again or contact support.");
  //   }
  // };

  const handleRecallInvoice = async () => {
    setReasonAction("recall");
    setReasonNote("");
    setShowReasonModal(true);
  };
  
  const performRecallInvoice = async () => {
    try {
      const invNo = fullInvoice.invoiceNumber;
               
      await httpClient.put(
        `${API}/invoices/${invNo}/recall`,
        { recallReason: reasonNote },
        { headers: { Authorization: `Bearer ${Cookies.get("authToken")}`, "Office-Code": fullInvoice.billToOfficeCode } }
      );
      
      notify.success("✅ Invoice moved back to Drafts");
      onSave?.();
      onClose?.();
    } catch (err) {
      console.error("Recall failed:", err);
      notify.error("❌ Could not recall invoice. Please try again or contact support.");
    }
  };

  const handleReasonModalSubmit = async () => {
    if (!reasonNote.trim()) {
      notify.warning({ title: "Required", message: "Please provide a reason" });
      return;
    }
  
    try {
      const saveInvoiceNote = async () => {
        await httpClient.put(
          `${API}/invoices/${fullInvoice.uniqueId}/note`,
          { note: reasonNote },
          {
            headers: {
              "Authorization": `Bearer ${Cookies.get("authToken")}`,
              "Office-Code": fullInvoice.billToOfficeCode
            }
          }
        );
      };

      if (reasonAction === "reject") {
        // LGX-844: reason travels as `rejectReason` on the reject call itself — no separate
        // /note save, since `note` is a shared/general-purpose field the reason shouldn't hijack.
        // performRejectInvoice() already refreshes fullInvoice from the server response, which
        // includes the saved rejectReason.
        await performRejectInvoice();
      } else if (reasonAction === "recall") {
        await performRecallInvoice();
        await saveInvoiceNote();
        setFullInvoice(prev => ({ ...prev, note: reasonNote }));
      }

      setShowReasonModal(false);
      setReasonNote("");
    } catch (e) {
      console.error("Error:", e);
      notify.error({ 
        title: "Operation failed", 
        message: e?.response?.data?.message || "Could not complete action" 
      });
    }
  };
  const handleApproveCharge = async (chargeId) => {
    try {
      const { data: upd } = await httpClient.put(
        `${API}/invoice-charges/${chargeId}/discrepancy-status`,
        { status: "Resolved" },
        { headers: { Authorization: `Bearer ${Cookies.get("authToken")}`, "Office-Code": officeCode } }
      );
      dispatch(setLocalChargeStatus({ chargeId, status: upd.discrepancyStatus }));
  
      setFullInvoice(prev => {
        const base = Array.isArray(prev.shipments) ? prev.shipments : shipments;
  
        const updatedShipments = base.map(sh => ({
          ...sh,
          charges: (sh.charges || []).map(c =>
            c.chargeId === chargeId
              ? {
                  ...c,
                  discrepancyStatus: upd.discrepancyStatus,
                  chargeAuditStatus:
                    upd.discrepancyStatus === "Resolved" ? "Passed" : c.chargeAuditStatus,
                }
              : c
          ),
        }));
  
        return {
          ...prev,
          shipments: updatedShipments,
        };
      });
  
      notify.success({
        title: "Charge approved",
          message: "Discrepancy marked as Resolved."
        });
    } catch (e) {
      console.error(e);
      notify.error({
          title: "Approve failed",
          message: e?.response?.data?.message || "Could not approve charge."
         });
    }
  };

  const handleRejectCharge = async (chargeId) => {
    try {
      const { data: upd } = await httpClient.put(
        `${API}/invoice-charges/${chargeId}/discrepancy-status`,
        { status: "Rejected" },
        { headers: { Authorization: `Bearer ${Cookies.get("authToken")}`, "Office-Code": officeCode } }
      );
      dispatch(setLocalChargeStatus({ chargeId, status: upd.discrepancyStatus }));
  
      setFullInvoice(prev => {
        const base = Array.isArray(prev.shipments) ? prev.shipments : shipments;
        const updatedShipments = base.map(sh => ({
          ...sh,
          charges: (sh.charges || []).map(c =>
            c.chargeId === chargeId
              ? {
                  ...c,
                  discrepancyStatus: upd.discrepancyStatus,
                  chargeAuditStatus:
                    upd.discrepancyStatus === "Resolved"
                      ? "Passed"
                      : c.chargeAuditStatus,
                }
              : c
          ),
        }));
        return { ...prev, shipments: updatedShipments };
      });
  
      notify.warning({
        title: "Charge rejected",
        message: "Discrepancy marked as Rejected."
      });
    } catch (e) {
      console.error("Could not reject charge:", e);
      notify.error({
        title: "Reject failed",
        message: e?.response?.data?.message || "Could not reject charge."
      });
    }
  };
  
  if (loading) {
    return (
      <Loader
        color="text-blue-600"
        size="h-5 w-5"
        message="Loading Invoice..."
      />
    );
  }
  if (!fullInvoice) {
    return (
      <div className="p-4 text-center text-gray-600">
        No data found for this invoice.
      </div>
    );
  }

  const isApproved = (fullInvoice.invoiceStatus || "").toLowerCase() === "approved";


  const CHARGE_DISPLAY_NAMES = { FSC: 'Fuel Surcharge', BASE: 'Base Charge' };
  const getChargeDisplayName = (code, chargeName) =>
    CHARGE_DISPLAY_NAMES[code] ||
    (chargeName && chargeName !== code ? chargeName : null);

  const infoData = {
    "Invoice Number": { type: "text",   value: fullInvoice.invoiceNumber,  disabled: true },
    "Invoice Date":   { type: "text",   value: (() => { const d = fullInvoice.invoiceDate; if (!d) return ""; const [y, m, day] = d.split("T")[0].split("-"); return `${m}/${day}/${y}`; })(),    disabled: true },
    "Payment Terms":  { type: "text",   value: fullInvoice.paymentTerms,   disabled: true },
    "Currency":       { type: "text",   value: fullInvoice.currency,       disabled: true },
    "Invoice Total":  { type: "text",   value: formatMoney(fullInvoice.total, fullInvoice.currency), disabled: true }, // ✅ Changed
    "Invoice Status": { type: "text",   value: fullInvoice.invoiceStatus,  disabled: true },
    "Payment Status": { type: "text",   value: fullInvoice.paymentStatus || "Unpaid",  disabled: true },


  };



  
  const isPending = (fullInvoice.invoiceStatus || "").toLowerCase() === "pending";
  const isRejected = (fullInvoice.invoiceStatus || "").toLowerCase() === "rejected";
  const isDraft = (fullInvoice.invoiceStatus || "").toLowerCase() === "draft";

  const handleAddPayment = (invoice) => {
   setShowPaymentForm(true);
  };


  const headerButtons = [
    isPending && {
      label: "Recall",
      type: "secondary",
      color: "red",
      onClick: handleRecallInvoice,
        disabled: !canRecallInvoice,
    },
    isDraft && {
      label: "Delete",
      type: "danger",
      onClick: () => setShowDeleteConfirm(true),
      //icon: Trash2,
    },
    // (isDraft || isRejected) && !editingChargesMode && canEditInvoice &&  {
    //   label: "Edit Charges",
    //   type: "secondary",
    //   onClick: handleStartEditCharges,
    // },
    editingChargesMode && {
      label: "Save",
      type: "primary",
      onClick: handleSaveEditedCharges
    },
    editingChargesMode && {
      label: "Cancel",
      type: "secondary",
      onClick: handleCancelEditCharges
    },
    isPending && !isRejected && canApproveInvoice &&  {
      label: "Approve",
      type: "primary",
      onClick: () => handleApproveInvoice(fullInvoice.uniqueId, false),
      disabled: !allChargesResolved
    },
    isPending && !isRejected && canApproveInvoice && {
      label: "Approve and Pass",
      type: "primary",
      onClick: () => handleApproveInvoice(fullInvoice.uniqueId, true),
      disabled: !allChargesResolved
    },
    isPending && !isRejected && canRejectInvoice && {
      label: "Reject",
      type: "secondary",
      color: "red",
      onClick: handleRejectInvoice
    },
    isApproved && {
      label: "Add Payment Details",
      type: "secondary",
      onClick: () => handleAddPayment(fullInvoice),
    },
  ].filter(Boolean);


  const handleSavePayment = async () => {
    try {
      // Filter out empty payments
      const validPayments = paymentData.filter(p =>
        p.checkNumber || p.payDate || p.paymentAmount
      );

      if (validPayments.length === 0) {
        notify.warning({
          title: "No payment data",
          message: "Please fill in at least one payment before saving.",
        });
        return;
      }

      const savedPayments = await Promise.all(
        validPayments.map(payment =>
          httpClient.post(
            `${API}/payments`,
            {
              ...payment,
              // InvoiceDTO exposes the PK as `uniqueId`, not `id`
              invoiceId: fullInvoice.uniqueId ?? fullInvoice.id,
              invoiceNumber: fullInvoice.invoiceNumber,
              // invoice numbers are unique per carrier, so the carrier pins which invoice
              payToOfficeCode: fullInvoice.payToOfficeCode,
            },
            {
              headers: {
                Authorization: `Bearer ${Cookies.get("authToken")}`,
                // shipper's office — must be one the logged-in user belongs to, or the backend 403s
                "Office-Code": fullInvoice.billToOfficeCode,
              },
            }
          ).then(res => res.data)
        )
      );
  

    // refetch by id — the number is only unique per carrier, so by-number is ambiguous
    const refreshId = fullInvoice.uniqueId ?? fullInvoice.id;
    const { data: refreshedInvoice } = await httpClient.get(
      refreshId
        ? `${API_BASE_URL}/invoices/${encodeURIComponent(refreshId)}`
        : `${API_BASE_URL}/invoices/number/${encodeURIComponent(fullInvoice.invoiceNumber)}`,
      {
        headers: {
          Authorization: `Bearer ${Cookies.get("authToken")}`,
          "Office-Code": fullInvoice.billToOfficeCode,
        },
      }
    );

    setFullInvoice(refreshedInvoice);
  
      notify.success({
        title: "Payment saved",
        message: `${savedPayments.length} payment(s) successfully added.`,
      });

      setShowPaymentForm(false);
      setPaymentData([{ checkNumber: "", payDate: "", paymentAmount: "" }]);

    } catch (err) {
      console.error(err);
      notify.error({
        title: "Save failed",
        message: err?.response?.data?.message || "Could not save payment.",
      });
    }
  };

  const handleAddPaymentRow = () => {
    setPaymentData(prev => [...prev, { checkNumber: "", payDate: "", paymentAmount: "",currency: "USD" }]);
  };

  const handleRemovePaymentRow = (index) => {
    setPaymentData(prev => prev.filter((_, i) => i !== index));
  };

  const handlePaymentChange = (index, field, value) => {
    setPaymentData(prev => prev.map((payment, i) =>
      i === index ? { ...payment, [field]: value } : payment
    ));
  };




  return (
    <div className="w-full h-full min-h-0 flex flex-col transition-opacity duration-500 bg-gray-50">
     <div
        ref={headerRef}
        className="sticky top-0 z-10 bg-white shadow-md w-full"
      >
        <StickyHeader
          infoData={infoData}
          onInfoChange={() => {}}
          templates={["Template 1", "Template 2", "Template 3"]}
          onSelectTemplate={() => {}}
          buttons={headerButtons}
          saveTemplateConfig={{ checked: false, onChange: () => {} }}
          mode="view" 
        />
      </div>

        <div className="flex-1 overflow-y-auto
            bg-white dark:bg-acs-dark-surface
            w-full p-fluid-1
            scroll-container overscroll-contain
            text-gray-800 dark:text-acs-dark-text">
        <div className="p-fluid-2">
          <h3 className="text-fluid-lg font-semibold mb-fluid-2" >
            Invoice Information
          </h3>

 

  {/* ✅ Display Note if it exists */}
  {fullInvoice?.note && (
    <div className="mb-fluid-2 p-fluid-2 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Note</p>
      <p className="text-sm text-gray-800 dark:text-acs-dark-text whitespace-pre-wrap">{fullInvoice.note}</p>
    </div>
  )}

          <div className="grid grid-cols-2 gap-4 mb-fluid-2 bg-white dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text">


            <ExpandableHeader
              headerSection="Bill To"
              icon={CreditCard}
              headerType="primary"
              disableToggle={true} // makes it static
            >
              <div className="grid grid-cols-3 gap-x-6 gap-y-3 p-fluid-3 text-fluid-base text-gray-800 bg-white dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text" >
                {Object.entries({
                  "Company": fallbackName(fullInvoice.billToCompanyName|| ""),
                  "Address": fullInvoice.billToAddress,
                  "City": fullInvoice.billToCity,
                  "State": fullInvoice.billToState,
                  "Country": fullInvoice.billToCountry,
                  "Zip": fullInvoice.billToZip,
                  "Phone": fullInvoice.billToPhone,
                  "Email": fullInvoice.billToEmail,
                  "Fax": fullInvoice.billToFax,
                }).map(([label, value]) => (
                  <DisplayDetail
                    key={label}
                    label={label}
                    value={value ?? ""}
                    containerClass="bg-[#e6e6f9]"
                    valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
                    labelClass="text-dense-xs text-gray-500"
                  />
                ))}
              </div>
            </ExpandableHeader>

            <ExpandableHeader
              headerSection="Pay To"
              icon={Wallet}
              headerType="primary"
              disableToggle={true}
            >
              <div className="grid grid-cols-3 gap-x-6 gap-y-3 p-fluid-3 text-fluid-base bg-white dark:bg-acs-dark-surface
                        text-gray-800 dark:text-acs-dark-text" >
                {Object.entries({
                  "Company": fullInvoice.payToCompanyName,
                  "Address": fullInvoice.payToAddress,
                  "City": fullInvoice.payToCity,
                  "State": fullInvoice.payToState,
                  "Country": fullInvoice.payToCountry,
                  "Zip": fullInvoice.payToZip,
                  "Phone": fullInvoice.payToPhone,
                  "Email": fullInvoice.payToEmail,
                  "Fax": fullInvoice.payToFax,
                }).map(([label, value]) => (
                  <DisplayDetail
                    key={label}
                    label={label}
                    value={value ?? ""}
                    containerClass="bg-[#e6e6f9]"
                    valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
                    labelClass="text-dense-xs text-gray-500"
                  />
                ))}
              </div>
            </ExpandableHeader>

          </div>


            <h3 className="text-fluid-lg font-semibold mb-fluid-2" >
              Shipment Details
            </h3>

           {shipments.map((sh, i) => (
              <ExpandableHeader
                key={i}
                headerSection="Shipment"
                headerLabel={`${i + 1} – ${sh.shipmentNumber || "N/A"}`}
                icon={Truck} // or Package if you prefer
                headerType="primary"

                disableToggle={false}// keep initially open if you want
              >
                {/* === Shipment Info Grid === */}
                <div className="px-fluid-3 py-fluid-3 bg-white rounded-b-md space-y-fluid-4 dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
  
  {/* First Row - 5 fields */}
  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
    {Object.entries({
      "Shipment Number": sh.shipmentNumber,
      "Origin": sh.shipmentOrigin,
      "Carrier Code": fullInvoice.payToOfficeCode,
      "Destination": sh.shipmentDestination,
      "Equip. Number": sh.equipmentNumber,
    }).map(([label, value]) => (
      <DisplayDetail
        key={label}
        label={label}
        value={value ?? ""}
        containerClass="bg-[#e6e6f9]"
        valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
        labelClass="text-dense-xs text-gray-500"
      />
    ))}
  </div>

  {/* Second Row - 5 fields */}
  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
    {Object.entries({
      "Shipment Total Charge": formatMoney(sh.shipmentCharge, fullInvoice.currency),
      "Last Approved By": fullInvoice.approvedBy || "—",
      "Approval Date": fullInvoice.approvalDate
        ? new Date(fullInvoice.approvalDate).toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "—",
      "Last Rejected By": fullInvoice.rejectedBy || "—",
      "Last Rejection Date": fullInvoice.rejectedDate
        ? new Date(fullInvoice.rejectedDate).toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "—",
      "Rejection Reason": fullInvoice.rejectReason || "—",
    }).map(([label, value]) => (
      <DisplayDetail
        key={label}
        label={label}
        value={value ?? ""}
        containerClass="bg-[#e6e6f9]"
        valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
        labelClass="text-dense-xs text-gray-500"
      />
    ))}
  </div>

  {/* Third Row - Created By / Created Date */}
  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
    {Object.entries({
      "Created By": fullInvoice.createdBy || "—",
      "Created Date": fullInvoice.createdDate
        ? new Date(fullInvoice.createdDate).toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          })
        : "—",
    }).map(([label, value]) => (
      <DisplayDetail
        key={label}
        label={label}
        value={value ?? ""}
        containerClass="bg-[#e6e6f9]"
        valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
        labelClass="text-dense-xs text-gray-500"
      />
    ))}
  </div>


                  {/* === Charges Section === */}
                  <ExpandableHeader
                    headerSection="Charges"
                    icon={DollarSign}
                    headerType="secondary"
                    disableToggle={false} // static header — not collapsible
                  >

                    
                    
                    {/* === Charges Grid === */}
                    {(sh.charges || []).map((c, ci) => {
                                            const displayAudit =
                        overrides[c.chargeId] != null
                          ? overrides[c.chargeId]
                          : c.chargeAuditStatus ||
                            (["Resolved", "No Discrepancy"].includes(c.discrepancyStatus)
                              ? "Passed"
                              : "Failed");

                      return (
                        <div
                        key={ci}
                        className={`grid grid-cols-1 sm:grid-cols-2
                         ${isShipper ? "lg:grid-cols-7" : "lg:grid-cols-6"}
                        gap-4 bg-white p-fluid-3 text-fluid-base
                        text-gray-800 dark:bg-acs-dark-surface
                        dark:text-acs-dark-text`}
                      >
                        {/* 1. Charge label */}


                        {/* 2. Charge (Code + Name combined) */}
                        <DisplayDetail
                          label="Charge"
                          value={[c.chargeCode, getChargeDisplayName(c.chargeCode, c.chargeName)].filter(Boolean).join(' - ') || "—"}
                          containerClass="bg-[#e6e6f9]"
                          valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
                          labelClass="text-dense-xs text-gray-500"
                        />

                        {/* 4. Invoiced Amount */}
                        <div className="flex flex-col">
                          <p className="text-dense-xs text-gray-500">Invoiced Amount</p>
                          {editingChargesMode ? (
                            <input
                              type="number"
                              step="0.01"
                              value={
                                editedShipments?.[i]?.charges?.[ci]?.chargeAmount ??
                                c.chargeAmount ??
                                ""
                              }
                              onChange={(e) =>
                                handleEditedChargeChange(i, ci, "chargeAmount", e.target.value)
                              }
                              className="w-full border border-gray-300 rounded px-2 py-1 min-h-[2.5rem]"
                            />
                          ) : (
                            <p className="border border-gray-300 rounded px-2 py-1 min-h-[2.5rem] bg-[#e6e6f9]">
                              {formatMoney(c.chargeAmount, c.chargeCurrency)}
                            </p>
                          )}
                        </div>
                      
                        {/* 5. Currency */}
                        <DisplayDetail
                          label="Currency"
                          value={c.chargeCurrency ?? "USD"}
                          containerClass="bg-[#e6e6f9]"
                          valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1"
                          labelClass="text-dense-xs text-gray-500"
                        />


                        
                      
                        {/* 6. Discrepancy Amount */}
                        <DisplayDetail
                          label="Discrepancy Amount"
                          value={formatMoney(c.discrepancyAmount, c.chargeCurrency)}
                          containerClass="bg-[#e6e6f9]"
                          valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1"
                          labelClass="text-dense-xs text-gray-500"
                        />
                      
                        {/* 7. Audit / Discrepancy Status */}
                        <DisplayDetail
                          label="Audit Status"
                          value={c.discrepancyStatus}
                          containerClass="bg-[#e6e6f9]"
                          valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
                          labelClass="text-dense-xs text-gray-500"
                        />

                        {/* Shipment Charge (Estimated) */}
<DisplayDetail
  label="Shipment Charge"
  value={formatMoney(
    getEstimatedAmount(sh.shipmentNumber, c.chargeCode),
    c.chargeCurrency
  ) ?? "—"}
  containerClass="bg-[#e6e6f9]"
  valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1"
  labelClass="text-dense-xs text-gray-500"
/>

{/* Tolerance */}
{isShipper && (
<div className="flex flex-col">
  <p className="text-dense-xs text-gray-500">Tolerance</p>
  <div className="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 bg-[#e6e6f9] flex items-center justify-between gap-2">
    <span>{c.chargeCode ? formatTolerance(c.chargeCode) : "—"}</span>
    {c.chargeCode && toleranceMap[c.chargeCode] && (
      <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
        toleranceMap[c.chargeCode].toleranceBasis === "PERCENT"
          ? "bg-blue-100 text-blue-700"
          : "bg-green-100 text-green-700"
      }`}>
        {toleranceMap[c.chargeCode].toleranceBasis}
      </span>
    )}
  </div>
</div>
   )}                   
                        {/* Action Buttons row – full width under the 7 columns */}
                        {c.discrepancyStatus === "Discrepancy" && (
                          <div className={`col-span-1 sm:col-span-2 ${
                            isShipper ? "lg:col-span-7" : "lg:col-span-6"
                          }`}>
                            <div className="flex items-center justify-end space-x-2 pt-2 border-t border-gray-200 dark:border-acs-dark-border mt-2">
                              {canApproveCharge && !isRejected && (
                                <button
                                  onClick={() => handleApproveCharge(c.chargeId)}
                                  className="
                                    flex items-center space-x-1 px-3 py-1 rounded text-sm transition-none
                                    bg-green-600 text-white dark:text-black hover:bg-green-700
                                    dark:bg-emerald-500 dark:hover:bg-emerald-400
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                  "
                                >
                                  <CheckCircle size={14} />
                                  <span>Approve</span>
                                </button>
                              )}
                              {canRejectCharge && !isRejected && (
                                <button
                                  onClick={() => handleRejectCharge(c.chargeId)}
                                  className="
                                    flex items-center space-x-1 px-3 py-1 rounded text-sm transition-none
                                    bg-red-600 text-white hover:bg-red-700
                                    dark:bg-rose-500 dark:hover:bg-rose-400 dark:text-black
                                    disabled:opacity-50 disabled:cursor-not-allowed
                                  "
                                >
                                  <XCircle size={14} />
                                  <span>Reject</span>
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                              );
                    })}
                  </ExpandableHeader>



                  <ExpandableHeader
                    headerSection={`Ship To (${(sh.shipToInfo || []).length} ${
                      (sh.shipToInfo || []).length === 1 ? "Address" : "Addresses"
                    })`}
                    icon={MapPin}
                    headerType="tertiary"
                    disableToggle={true} // static, no dropdown
                  >
                    {(sh.shipToInfo || []).map((shipTo, shipToIdx) => (
                      <div key={shipToIdx} className="space-y-2">

                        {/* Show header only when multiple addresses */}
                        {(sh.shipToInfo || []).length > 1 && (
                          <div className="bg-gray-200 px-fluid-2 py-1">
                            <p className="text-sm font-semibold text-gray-700">
                              Ship To Address {shipToIdx + 1}
                            </p>
                          </div>
                        )}

                          {/* Ship-to details grid */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 bg-white p-fluid-3 text-fluid-base dark:bg-acs-dark-surface
                          text-gray-800 dark:text-acs-dark-text">
                            {Object.entries({
                              "Company": shipTo.shipToCompanyName,
                              "Address": shipTo.shipToAddress,
                              "City": shipTo.shipToCity,
                              "State": shipTo.shipToState,
                              "Country": shipTo.shipToCountry,
                              "Zip": shipTo.shipToZip,
                              "Phone": shipTo.shipToPhone,
                              "Email": shipTo.shipToEmail,
                            }).map(([label, value]) => (
                              <DisplayDetail
                                key={label}
                                label={label}
                                value={value ?? ""}
                                containerClass="bg-[#e6e6f9]"
                                valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
                                labelClass="text-dense-xs text-gray-500"
                              />
                            ))}
                          </div>
                      </div>
                    ))}
                  </ExpandableHeader>
          
              

                  </div>

                </ExpandableHeader>


            ))}

<h3 className="text-fluid-lg font-semibold mb-fluid-2 mt-fluid-2">
  Payment Details
</h3>

<ExpandableHeader
  headerSection={`Payments ${fullInvoice.payments && fullInvoice.payments.length > 0 ? `(${fullInvoice.payments.length})` : ''}`}
  icon={DollarSign}
  headerType="primary"
  disableToggle={false}
>
  {fullInvoice.payments && fullInvoice.payments.length > 0 ? (
    <div className="space-y-4 bg-white p-fluid-3 dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
      {fullInvoice.payments.map((p, idx) => (
        <div key={idx}>
          {/* Show header only when multiple payments */}
          {fullInvoice.payments.length > 1 && (
            <div className="bg-gray-200 px-fluid-2 py-1">
              <p className="text-sm font-semibold text-gray-700">
                Payment {idx + 1}
              </p>
            </div>
          )}
          
          {/* Payment details grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 bg-white p-fluid-3 text-fluid-base dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
            <DisplayDetail
              label="Check Number/Payment ID"
              value={p.checkNumber ?? ""}
              containerClass="bg-[#e6e6f9]"
              valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
              labelClass="text-dense-xs text-gray-500"
            />
            <DisplayDetail
              label="Pay Date"
              value={p.payDate ?? ""}
              containerClass="bg-[#e6e6f9]"
              valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
              labelClass="text-dense-xs text-gray-500"
            />
            <div className="grid grid-cols-2 gap-4">
              <DisplayDetail
                label="Amount"
                value={formatMoney(p.paymentAmount, p.currency)} 
                containerClass="bg-[#e6e6f9]"
                valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
                labelClass="text-dense-xs text-gray-500"
              />
              <DisplayDetail
                label="Currency"
                value={p.currency ?? "USD"}
                containerClass="bg-[#e6e6f9]"
                valueClass="border border-gray-300 rounded min-h-[2.5rem] px-2 py-1 overflow-x-auto whitespace-nowrap scrollbar-hide"
                labelClass="text-dense-xs text-gray-500"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="bg-white p-fluid-3 dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
      <p className="text-gray-500 italic">No payments yet</p>
    </div>
  )}
</ExpandableHeader>
{showPaymentForm && (
  <ExpandableHeader
    headerSection="Add Payment Details"
    icon={DollarSign}
    headerType="primary"
    disableToggle={true}
  >
    <div className="space-y-4 bg-white p-fluid-3 dark:bg-acs-dark-surface text-gray-800 dark:text-acs-dark-text">
      {paymentData.map((payment, index) => (
        <div key={index}>
          {paymentData.length > 1 && (
            <div className="bg-gray-200 px-fluid-2 py-1 flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-gray-700">
                Payment {index + 1}
              </p>
              {paymentData.length > 1 && (
                <button
                  onClick={() => handleRemovePaymentRow(index)}
                  className="text-red-600 hover:text-red-800 text-sm"
                >
                  Remove
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div>
              <p className="text-dense-xs text-gray-500">Check Number/Payment ID</p>
              <input
                type="text"
                value={payment.checkNumber}
                onChange={(e) =>
                  handlePaymentChange(index, "checkNumber", e.target.value)
                }
                className="w-full border border-gray-300 rounded px-2 py-1 min-h-[2.5rem]"
              />
            </div>

            {/* Pay Date */}
           {/* Pay Date */}
<div>
  <p className="text-dense-xs text-gray-500">Pay Date</p>
  <div className="relative">
    <input
      ref={(el) => {
        paymentDatePickersRef.current = {
          ...paymentDatePickersRef.current,
          [`payment-${index}-payDate`]: el,
        };
      }}
      type="date"
      value={payment.payDate}
      onChange={(e) =>
        handlePaymentChange(index, "payDate", e.target.value)
      }
      className="w-full border border-gray-300 rounded px-2 py-1 min-h-[2.5rem] pr-8"
    />
    <button
      type="button"
      onClick={() =>
        paymentDatePickersRef.current[`payment-${index}-payDate`]?.showPicker()
      }
      className="absolute right-2 top-1/2 -translate-y-1/2 
                text-gray-500 dark:text-acs-dark-subtext 
                hover:text-blue-600 dark:hover:text-[#3B5CCC]
                transition-none"
    >
      <Calendar size={16} />
    </button>
  </div>
</div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-dense-xs text-gray-500">Payment Amount</p>
                <input
                  type="number"
                  step="0.01"
                  value={payment.paymentAmount}
                  onChange={(e) =>
                    handlePaymentChange(index, "paymentAmount", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded px-2 py-1 min-h-[2.5rem]"
                />
              </div>

              <div>
                <p className="text-dense-xs text-gray-500">Currency</p>
                <select
                  value={payment.currency}
                  onChange={(e) =>
                    handlePaymentChange(index, "currency", e.target.value)
                  }
                  className="w-full border border-gray-300 rounded px-2 py-1 min-h-[2.5rem]"
                >
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="MXN">MXN</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-acs-dark-border mt-4">
        <button
          onClick={handleAddPaymentRow}
          className="px-4 py-2 rounded text-sm bg-gray-600 text-white hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-400"
        >
          Add Another Payment
        </button>

        <div className="flex space-x-2">
          <button
            onClick={() => {
              setShowPaymentForm(false);
              setPaymentData([{ checkNumber: "", payDate: "", paymentAmount: "" }]);
            }}
            className="px-4 py-2 rounded text-sm bg-gray-400 text-white hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            onClick={handleSavePayment}
            className="px-4 py-2 rounded text-sm bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400"
          >
            Save Payment(s)
          </button>
        </div>
      </div>
    </div>
  </ExpandableHeader>
)}
          </div>
        </div>
        {showDeleteConfirm && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-acs-dark-surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <Trash2 className="h-6 w-6 text-red-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-acs-dark-text mb-2">
            Delete Draft Invoice
          </h3>
          <p className="text-sm text-gray-600 dark:text-acs-dark-subtext mb-4">
            Are you sure you want to delete this draft invoice? This action cannot be undone.
          </p>
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 rounded text-sm bg-gray-400 text-white hover:bg-gray-500 transition-none"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteDraftInvoice}
              className="px-4 py-2 rounded text-sm bg-red-600 text-white hover:bg-red-700 transition-none flex items-center space-x-1"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

{showReasonModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-acs-dark-surface rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
      <h3 className="text-lg font-semibold mb-2 dark:text-acs-dark-text">
        {reasonAction === "reject" ? "Reject Invoice" : "Recall Invoice"}
      </h3>
      <p className="text-sm text-gray-600 dark:text-acs-dark-subtext mb-4">
        {reasonAction === "reject"
          ? "Please add a rejection reason. This will be included in the notification email sent to the carrier."
          : "Please add a note:"}
        {" "}<span className="text-red-500">*</span>
      </p>
      <textarea
        value={reasonNote}
        onChange={(e) => setReasonNote(e.target.value)}
        placeholder={reasonAction === "reject" ? "Enter rejection reason..." : "Enter note..."}
        className="w-full border border-gray-300 rounded px-3 py-2 min-h-[100px] dark:bg-acs-dark-surface dark:border-acs-dark-border dark:text-acs-dark-text focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex justify-end space-x-2 mt-4">
        <button
          onClick={() => { setShowReasonModal(false); setReasonNote(""); }}
          className="px-4 py-2 bg-gray-400 text-white rounded hover:bg-gray-500"
        >
          Cancel
        </button>
        <button
          onClick={handleReasonModalSubmit}
          disabled={!reasonNote.trim()}
          className={`px-4 py-2 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed ${reasonAction === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}`}
        >
          {reasonAction === "reject" ? "Reject" : "Recall"}
        </button>
      </div>
    </div>
  </div>
)}
      </div>

  );
};

export default ViewInvoiceTab;
